import React from 'react';

import { SEARCH_OPERATORS, SORT_OPTIONS } from '../../constants';
import {
  SearchForm,
  type SearchFormData,
  type SearchTypeOption,
  type FilterChipConfig,
  type MoreSectionConfig,
} from '../shared';

export type OpinionsSearchType =
  | 'keyword'
  | 'citation'
  | 'docketNumber'
  | 'caseName'
  | 'judgeName';

export interface OpinionsSearchFormData
  extends Omit<SearchFormData, 'searchType'> {
  searchType: OpinionsSearchType;
}

export interface OpinionsSearchFormProps {
  onSubmit?: (data: OpinionsSearchFormData) => void;
  initialSearchType?: OpinionsSearchType;
  initialQuery?: string;
  initialExactMatch?: boolean;
  sortValue?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const OPINIONS_SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  { value: 'keyword', label: 'Keyword', placeholder: 'Search by Keyword' },
  {
    value: 'citation',
    label: 'Citation',
    placeholder: 'Enter citation (e.g., citation: 410 U.S. 113)',
    prefill: 'citation:',
  },
  {
    value: 'docketNumber',
    label: 'Docket Number',
    placeholder: 'Enter docket number (e.g., docketNumber: 12345)',
    prefill: 'docketNumber:',
  },
  {
    value: 'caseName',
    label: 'Case Name',
    placeholder: 'Enter case name (e.g., caseName: Roe v. Wade)',
    prefill: 'caseName:',
  },
  {
    value: 'judgeName',
    label: 'Judge Name',
    placeholder: 'Enter judge name (e.g., judge: Scalia)',
    prefill: 'judge:',
  },
];

const OPINIONS_FILTER_CHIP_CONFIGS: FilterChipConfig[] = [
  {
    id: 'jurisdiction',
    label: 'Jurisdiction',
    type: 'jurisdiction',
  },
  {
    id: 'caseName',
    label: 'Name',
    type: 'text',
    fieldLabel: 'Case Name',
    placeholder: 'e.g., Roe v. Wade',
  },
  {
    id: 'citation',
    label: 'Citation',
    type: 'text',
    fieldLabel: 'Citation',
    placeholder: 'e.g., 410 U.S. 113',
  },
  {
    id: 'docketNumber',
    label: 'Docket',
    type: 'text',
    fieldLabel: 'Docket Number',
    placeholder: 'e.g., 21-1234',
  },
  {
    id: 'judge',
    label: 'Judge Name',
    type: 'text',
    fieldLabel: 'Judge',
    placeholder: 'e.g., John Smith',
  },
];

const OPINIONS_MORE_SECTION_CONFIG: MoreSectionConfig = {
  id: 'more',
  label: 'More',
  popoverTitle: 'Add More Details',
  fields: [
    {
      id: 'orderBy',
      label: 'Search Results Order',
      type: 'select',
      options: SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.display,
      })),
      defaultValue: 'score_desc',
    },
  ],
};

export const OpinionsSearchForm: React.FC<OpinionsSearchFormProps> = ({
  onSubmit,
  initialSearchType = 'keyword',
  initialQuery = '',
  initialExactMatch = true,
  sortValue,
  isLoading = false,
  disabled = false,
}) => {
  const handleSubmit = (data: SearchFormData) => {
    if (onSubmit) {
      onSubmit({
        ...data,
        searchType: data.searchType as OpinionsSearchType,
      });
    }
  };

  const initialFilters = sortValue
    ? { moreFilters: { orderBy: sortValue } }
    : undefined;

  return (
    <SearchForm
      title="Search Court Opinions Across 2000+ Courts"
      proTip="Use the search type chips below to refine your search by keyword, citation, case name, docket number, or judge name."
      searchTypeOptions={OPINIONS_SEARCH_TYPE_OPTIONS}
      filterChipConfigs={OPINIONS_FILTER_CHIP_CONFIGS}
      moreSectionConfig={OPINIONS_MORE_SECTION_CONFIG}
      onSubmit={handleSubmit}
      initialSearchType={initialSearchType}
      initialQuery={initialQuery}
      initialExactMatch={initialExactMatch}
      initialFilters={initialFilters}
      isLoading={isLoading}
      disabled={disabled}
      showExactMatch={true}
      operatorsData={SEARCH_OPERATORS}
      testId="opinions-search-form"
    />
  );
};

export default OpinionsSearchForm;
